// Helpers de Tienda Nube reusables desde el front:
//  - ensureTnProducts: trae la lista de productos de la tienda de una marca;
//    si la marca no está conectada, abre el popup de OAuth y reintenta.
//  - fireTnImport: dispara el webhook de n8n que crea los productos elegidos.
// El access_token nunca pasa por acá (lo manejan las edge functions server-side).

import { supabase } from "./supabase";

const IMPORT_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/tiendanube-import";
const EXPORT_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/tiendanube-export";

export interface TnVariante {
  nombre: string;
  imgs: string[];
}

export interface TnProduct {
  id: number;
  nombre: string;
  /** Imagen principal (para la miniatura del grid). */
  img_src: string;
  /** Hasta 3 imágenes del producto (compat / caso sin colores). */
  imgs: string[];
  /** Variantes a crear (1..N). N>1 = producto con colores. */
  variantes: TnVariante[];
  /** Nombres de color para los circulitos del grid (opcional). */
  colores?: string[];
}

interface TnProductsResponse {
  connected: boolean;
  products?: TnProduct[];
  store_name?: string | null;
  /** true si la empresa YA tiene otra tienda conectada (para avisar al conectar una 2ª). */
  company_has_stores?: boolean;
}

async function fetchTnProducts(
  brandId: string | null,
): Promise<TnProductsResponse> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId },
  });
  if (error)
    throw new Error(error.message || "No se pudo traer la lista de productos.");
  return data as TnProductsResponse;
}

/**
 * Abre la ventana del OAuth. **Llamar SIEMPRE de forma SINCRÓNICA dentro del
 * handler del click** — nunca después de un `await` (ADR-2026-08-07-tiendanube-popup-safari).
 *
 * Motivo: los navegadores solo dejan abrir ventanas mientras dura la *transient
 * activation* que arranca con el gesto del usuario. Chrome y Firefox estiraron
 * ese permiso a ~5s (por eso el bug no se veía ahí), pero **WebKit lo corta
 * apenas la promesa cede el turno**: cualquier `await` en el medio y
 * `window.open` devuelve `null`. Como iOS obliga a todos los navegadores a usar
 * WebKit, en iPhone no funcionaba en ninguno.
 *
 * Se abre sobre una página propia de carga (mismo origen) y no sobre la URL de
 * autorización, porque esa URL todavía no la tenemos: hay que pedirla a la edge
 * function `tn-connect`. Una vez que llega, se navega la ventana con
 * `connectTnEnVentana`.
 */
export function abrirVentanaOAuth(): Window | null {
  return window.open("/tn-conectando.html", "tn_connect", "width=620,height=740");
}

/** Trae los productos de la tienda; si no está conectada, abre el popup de
 *  OAuth y reintenta. Devuelve los productos + el nombre de la tienda.
 *
 *  `opts.popup` es la ventana que el caller YA abrió en el click (ver
 *  `abrirVentanaOAuth`). Este flujo consulta los productos ANTES de saber si
 *  hace falta conectar, así que el caller la abre "por las dudas" y acá se
 *  **cierra sola** si la tienda ya estaba conectada. */
export async function ensureTnProducts(
  brandId: string | null,
  opts?: { confirmNewStore?: () => Promise<boolean>; popup?: Window | null },
): Promise<{ products: TnProduct[]; storeName: string | null }> {
  const cerrarPopup = () => {
    try {
      opts?.popup?.close();
    } catch {
      /* noop */
    }
  };

  let res: TnProductsResponse;
  try {
    res = await fetchTnProducts(brandId);
  } catch (e) {
    cerrarPopup(); // no dejar la ventana de carga colgada si falla la consulta
    throw e;
  }

  if (!res.connected) {
    // Si la empresa YA tiene otra tienda conectada (en otra marca), avisamos
    // antes de conectar una nueva: la que se conecta = la que el comerciante
    // tenga logueada en Tiendanube (no la podemos elegir nosotros — ver ADR-093).
    if (res.company_has_stores && opts?.confirmNewStore) {
      const ok = await opts.confirmNewStore();
      if (!ok) {
        cerrarPopup();
        throw new Error("CANCELLED");
      }
    }
    try {
      await connectTnEnVentana(brandId, opts?.popup);
    } catch (e) {
      cerrarPopup();
      throw e;
    }
    res = await fetchTnProducts(brandId);
  } else {
    cerrarPopup(); // ya estaba conectada: la ventana no hacía falta
  }

  if (!res.connected) throw new Error("No se pudo conectar la tienda.");
  return { products: res.products || [], storeName: res.store_name || null };
}

/**
 * Inicia el OAuth de Tiendanube sobre una ventana **ya abierta por el gesto del
 * usuario** (`abrirVentanaOAuth`), y espera la confirmación. NO trae productos.
 *
 * Si `popup` viene `null`/cerrada, el navegador bloqueó la ventana: se avisa con
 * un mensaje accionable en vez de fallar en silencio.
 */
export async function connectTnEnVentana(
  brandId: string | null,
  popup: Window | null | undefined,
): Promise<void> {
  if (!popup)
    throw new Error("Permití las ventanas emergentes para conectar tu tienda.");

  const { data, error } = await supabase.functions.invoke("tn-connect", {
    body: { brand_id: brandId },
  });
  const authorizeUrl = (data as { authorizeUrl?: string } | null)?.authorizeUrl;
  if (error || !authorizeUrl) {
    try {
      popup.close(); // no dejar la ventana de carga colgada
    } catch {
      /* noop */
    }
    throw new Error(
      (error as Error | null)?.message || "No se pudo iniciar la conexión.",
    );
  }

  // El usuario ya cerró la ventana de carga mientras pedíamos la URL.
  if (popup.closed) throw new Error("Conexión cancelada.");

  popup.location.href = authorizeUrl;
  await waitForConnect(popup);
}

// NOTA: acá vivía `connectTn(brandId)`, que abría la ventana y hacía todo junto.
// Se eliminó a propósito: era una API que PARECÍA segura pero solo funcionaba si
// el caller la invocaba sin ningún `await` antes — justo el error que causó
// ADR-2026-08-07-tiendanube-popup-safari. El par `abrirVentanaOAuth()` (en el
// click) + `connectTnEnVentana(brandId, popup)` (después) deja el requisito a la
// vista y no se puede usar mal sin darse cuenta.

/** Dispara la importación en n8n (corre en background). Tolera que CORS
 *  bloquee la lectura de la respuesta: el request igual llega. */
export async function fireTnImport(params: {
  userId: string | undefined;
  companyId: string | null;
  brandId: string | null;
  productos: {
    nombre: string;
    imgs: string[];
    tn_product_id: number;
    variantes: { nombre: string; imgs: string[] }[];
  }[];
}): Promise<void> {
  try {
    await fetch(IMPORT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: params.userId,
        id_company: params.companyId,
        brand_id: params.brandId,
        productos: params.productos,
      }),
    });
  } catch {
    /* dispara igual aunque CORS bloquee la lectura */
  }
}

/** Un producto a exportar: su acción (agregar/reemplazar/crear) + sus fotos en orden. */
export interface TnExportProducto {
  producto_id: string; // UUID de Atlas (para vincular tn_product_id al crear)
  action: "add" | "replace" | "create";
  tn_product_id: number | null; // si add/replace; null si create
  // solo si create; stock/sku "" = omitir en TN
  nuevo?: {
    nombre: string;
    precio: string;
    descripcion: string;
    stock: string;
    sku: string;
  };
  imagenes: { imagen_generada_id: string; src: string; orden: number }[];
}

/** Dispara la exportación en n8n (corre en background). Tolera que CORS bloquee
 *  la lectura de la respuesta: el request igual llega. Contrato per-producto. */
export async function fireTnExport(params: {
  userId?: string;
  companyId: string | null;
  brandId: string | null;
  productos: TnExportProducto[];
}): Promise<void> {
  try {
    await fetch(EXPORT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: params.userId,
        id_company: params.companyId,
        brand_id: params.brandId,
        productos: params.productos,
      }),
    });
  } catch {
    /* dispara igual aunque CORS bloquee la lectura */
  }
}

/** Datos actuales de UN producto de Tiendanube (para mostrar read-only en Atlas). */
export interface TnProductDetails {
  nombre: string;
  descripcion: string;
  variante_count: number;
  variante_id: number | null;
  precio: string | null;
  stock: number | null;
  sku: string | null;
}

/** Trae los datos actuales de un producto de TN (GET vía edge function tn-product). */
export async function getTnProductDetails(
  brandId: string | null,
  tnProductId: number,
): Promise<TnProductDetails | null> {
  const { data, error } = await supabase.functions.invoke("tn-product", {
    body: { brand_id: brandId, tn_product_id: tnProductId },
  });
  if (error) return null;
  return data as TnProductDetails;
}

/** Guarda cambios de un producto de TN (PUT vía edge function tn-product). */
export async function updateTnProduct(params: {
  brandId: string | null;
  tnProductId: number;
  varianteId: number | null;
  editarVariante: boolean;
  nombre: string;
  descripcion: string;
  precio: string;
  stock: string;
  sku: string;
}): Promise<boolean> {
  const { error } = await supabase.functions.invoke("tn-product", {
    body: {
      action: "update",
      brand_id: params.brandId,
      tn_product_id: params.tnProductId,
      variante_id: params.varianteId,
      editar_variante: params.editarVariante,
      nombre: params.nombre,
      descripcion: params.descripcion,
      precio: params.precio,
      stock: params.stock,
      sku: params.sku,
    },
  });
  return !error;
}

/** Trae TODOS los productos de la tienda (sin dedup) — para el picker "agregar a
 *  otro producto". Tira error si la invocación falla, para que el picker pueda
 *  distinguir "error de red/servidor" de "tienda vacía" (y ofrecer reintentar). */
export async function listTnProductsAll(
  brandId: string | null,
): Promise<TnProduct[]> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId, all: true },
  });
  if (error)
    throw new Error(
      error.message || "No se pudieron traer los productos de tu tienda.",
    );
  const d = data as { products?: TnProduct[] };
  return d?.products || [];
}

/** Estado de conexión de Tienda Nube de una marca (liviano: NO trae productos). */
export async function getTnStatus(
  brandId: string | null,
): Promise<{
  connected: boolean;
  storeName: string | null;
  companyHasStores: boolean;
}> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId, statusOnly: true },
  });
  if (error)
    return { connected: false, storeName: null, companyHasStores: false };
  const d = data as {
    connected?: boolean;
    store_name?: string | null;
    company_has_stores?: boolean;
  };
  return {
    connected: !!d?.connected,
    storeName: d?.store_name || null,
    companyHasStores: !!d?.company_has_stores,
  };
}

/** Desconecta la Tienda Nube de una marca (borra la integración). */
export async function disconnectTn(brandId: string | null): Promise<void> {
  const { error } = await supabase.functions.invoke("tn-disconnect", {
    body: { brand_id: brandId },
  });
  if (error) throw new Error(error.message || "No se pudo desconectar.");
}

// Resuelve cuando el popup confirma ('tn-connected' desde /tn-connected.html,
// mismo origen); rechaza si lo cierran antes o si se agota el tiempo.
function waitForConnect(popup: Window): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;

    const cleanup = () => {
      done = true;
      window.removeEventListener("message", onMessage);
      clearInterval(poll);
      clearTimeout(timeout);
      try {
        popup.close();
      } catch {
        /* noop */
      }
    };

    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; error?: string } | null;
      if (
        d?.type === "tn-connected" &&
        (e.source === popup || e.origin === window.location.origin)
      ) {
        cleanup();
        if (d.error) reject(new Error(d.error));
        else resolve();
      }
    };

    const poll = setInterval(() => {
      if (popup.closed && !done) {
        cleanup();
        reject(new Error("Conexión cancelada."));
      }
    }, 600);

    const timeout = setTimeout(() => {
      if (!done) {
        cleanup();
        reject(new Error("Se agotó el tiempo. Probá de nuevo."));
      }
    }, 180000);

    window.addEventListener("message", onMessage);
  });
}
